import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Cable, Calculator, FileSpreadsheet, Landmark } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { ModeBadge } from "./interfaces/sesStatus";
import SesHospedajes from "./interfaces/SesHospedajes";
import Accounting from "./interfaces/Accounting";
import ExcelExport from "./interfaces/ExcelExport";

const ICONS = { ses: Landmark, accounting: Calculator, excel: FileSpreadsheet };
const PAGES = { ses: SesHospedajes, accounting: Accounting, excel: ExcelExport };

// Admin → Interfaces: één scherm met bovenaan een tab per koppeling (SES.HOSPEDAJES, boekhouding, …).
export default function AdminInterfaces() {
  const { t } = useLang();
  const { iface } = useParams();
  const navigate = useNavigate();
  const [list, setList] = useState([]);

  useEffect(() => {
    api.get("/interfaces").then(({ data }) => setList(data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, []);

  const active = list.find((i) => i.id === iface && i.available) || list.find((i) => i.available);
  useEffect(() => { if (active && active.id !== iface) navigate(`/admin/interfaces/${active.id}`, { replace: true }); }, [active, iface, navigate]);

  const Page = active ? PAGES[active.id] : null;

  return (
    <div className="space-y-5" data-testid="interfaces-panel">
      <div className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
        <div className="px-5 pt-4 flex items-center gap-2 text-sm text-muted-foreground"><Cable className="h-4 w-4" />{t("admin_interfaces_desc")}</div>
        <div className="mt-3 flex gap-1 overflow-x-auto px-3" role="tablist" data-testid="iface-tabs">
          {list.map((i) => {
            const Icon = ICONS[i.id] || Cable;
            const on = active?.id === i.id;
            return (
              <button key={i.id} type="button" role="tab" aria-selected={on} disabled={!i.available} onClick={() => navigate(`/admin/interfaces/${i.id}`)}
                data-testid={`iface-tab-${i.id}`}
                className={`relative flex items-center gap-2 whitespace-nowrap rounded-t-xl px-4 py-3 text-sm font-semibold transition-colors ${on
                  ? "bg-background text-foreground border border-b-0 border-border"
                  : i.available ? "text-muted-foreground hover:bg-muted/60" : "text-muted-foreground/50 cursor-not-allowed"}`}>
                <Icon className="h-4 w-4" />{i.name}
                {i.id === "ses" ? <ModeBadge mode={i.mode} t={t} /> : !i.available ? <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold">{t("iface_soon")}</span> : null}
                {i.id === "ses" && (i.open > 0 || i.rejected > 0) && (
                  <span className={`ml-1 rounded-full px-1.5 py-0.5 font-mono text-[10px] font-bold ${i.rejected ? "bg-red-600 text-white" : "bg-sky-600 text-white"}`} data-testid={`iface-tab-count-${i.id}`}>{i.rejected || i.open}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>
      {Page && <Page />}
    </div>
  );
}
